import { useApi } from "@/api";
import {
  ContentType,
  TAccount,
  TAccountType,
  TColor,
  TCurrency,
  TIcon,
} from "@/api/v1_0";
import { RootStore } from "@/store/root";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";
import { makeAutoObservable } from "mobx";
import { months } from "./consts";
import {
  IAccountAmount,
  ISummary,
  TAccountMap,
  TDirection,
  TMonthAccountMap,
  TMonthSummaryMap,
  TYearAccountMap,
  TYearSummaryMap,
} from "./types";

export class AnalyticsStore {
  private _rootStore: RootStore;

  public loading = false;
  public refreshing = false;

  public baseCurrency: TCurrency = TCurrency.RUB;
  public date: Date = new Date();
  public month: number = 1 + getMonth(new Date());
  public year: number = getYear(new Date());
  public direction: TDirection = "income";
  public amounts: TYearSummaryMap = new Map<number, TMonthSummaryMap>();
  public incomeSources: TYearAccountMap = new Map<number, TMonthAccountMap>();
  public expenseCategories: TYearAccountMap = new Map<
    number,
    TMonthAccountMap
  >();

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeAutoObservable(this);
  }

  public toggleCurrency = async (): Promise<void> => {
    switch (this.baseCurrency) {
      case TCurrency.EUR:
        this.baseCurrency = TCurrency.RUB;

        break;
      case TCurrency.RUB:
        this.baseCurrency = TCurrency.USD;

        break;
      case TCurrency.USD:
        this.baseCurrency = TCurrency.EUR;

        break;
    }

    await this.load(this.baseCurrency);
  };

  public load = async (
    currency: TCurrency | undefined = undefined
  ): Promise<void> => {
    this._rootStore.logging.track("analytics.load", {
      currency: currency || this.baseCurrency,
    });

    try {
      this.loading = true;

      const api = useApi();

      const {
        data: {
          currency: resultCurrency,
          accounts: resultAccounts = {},
          ...result
        },
      } = await api.v10.appHttpApiV10ControllerAnalyticsAmountsRead(
        {
          currency: currency || this.baseCurrency,
          precision: 2,
        },
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this.amounts = Object.keys(result).reduce(
        (yearsMap, year: string): Map<number, TMonthSummaryMap> =>
          yearsMap.set(
            parseInt(year, 10),
            Object.keys(result[year]).reduce(
              (monthsMap, month: string): TMonthSummaryMap =>
                monthsMap.set(parseInt(month, 10), {
                  ...result[year][month],
                  currency: resultCurrency,
                }),
              new Map<number, ISummary>()
            )
          ),
        new Map<number, TMonthSummaryMap>()
      );

      if (!this._rootStore.profile.pro) {
        this.incomeSources = new Map<number, TMonthAccountMap>([
          [
            this.year,
            new Map<number, TAccountMap>(
              [...months.keys()].map((monthIndex) => [
                monthIndex,
                new Map<TAccount["uuid"], IAccountAmount>([
                  [
                    "cf774398-32e1-4789-a9fe-3e5dfc705369",
                    {
                      account: {
                        uuid: "cf774398-32e1-4789-a9fe-3e5dfc705369",
                        type: TAccountType.IncomeSource,
                        name: "Здесь могла бы быть ваш источник дохода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Basket,
                        color: TColor.Value7DB437,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                  [
                    "777ea81a-143b-4292-9734-61d0c84b18be",
                    {
                      account: {
                        uuid: "777ea81a-143b-4292-9734-61d0c84b18be",
                        type: TAccountType.IncomeSource,
                        name: "Здесь могла бы быть ваш источник дохода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Book,
                        color: TColor.ValueE17DB9,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                  [
                    "e257629a-53a7-4e09-b62e-f9dd8d4cd555",
                    {
                      account: {
                        uuid: "e257629a-53a7-4e09-b62e-f9dd8d4cd555",
                        type: TAccountType.IncomeSource,
                        name: "Здесь могла бы быть ваш источник дохода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Mobile,
                        color: TColor.ValueE22C2C,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                  [
                    "c5ab7f02-5798-408e-ba09-260a98f8c463",
                    {
                      account: {
                        uuid: "c5ab7f02-5798-408e-ba09-260a98f8c463",
                        type: TAccountType.IncomeSource,
                        name: "Здесь могла бы быть ваш источник дохода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Advertisement,
                        color: TColor.Value2686DF,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                ]),
              ])
            ),
          ],
        ]);
        this.expenseCategories = new Map<number, TMonthAccountMap>([
          [
            this.year,
            new Map<number, TAccountMap>(
              [...months.keys()].map((monthIndex) => [
                monthIndex,
                new Map<TAccount["uuid"], IAccountAmount>([
                  [
                    "cf774398-32e1-4789-a9fe-3e5dfc705369",
                    {
                      account: {
                        uuid: "cf774398-32e1-4789-a9fe-3e5dfc705369",
                        type: TAccountType.ExpenseCategory,
                        name: "Здесь могла бы быть ваша категория расхода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Basket,
                        color: TColor.Value7DB437,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                  [
                    "777ea81a-143b-4292-9734-61d0c84b18be",
                    {
                      account: {
                        uuid: "777ea81a-143b-4292-9734-61d0c84b18be",
                        type: TAccountType.ExpenseCategory,
                        name: "Здесь могла бы быть ваша категория расхода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Book,
                        color: TColor.ValueE17DB9,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                  [
                    "e257629a-53a7-4e09-b62e-f9dd8d4cd555",
                    {
                      account: {
                        uuid: "e257629a-53a7-4e09-b62e-f9dd8d4cd555",
                        type: TAccountType.ExpenseCategory,
                        name: "Здесь могла бы быть ваша категория расхода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Mobile,
                        color: TColor.ValueE22C2C,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                  [
                    "c5ab7f02-5798-408e-ba09-260a98f8c463",
                    {
                      account: {
                        uuid: "c5ab7f02-5798-408e-ba09-260a98f8c463",
                        type: TAccountType.ExpenseCategory,
                        name: "Здесь могла бы быть ваша категория расхода",
                        currency: TCurrency.RUB,
                        icon: TIcon.Advertisement,
                        color: TColor.Value2686DF,
                        balance: Math.floor(Math.random() * 100000),
                      },
                      amount: Math.floor(Math.random() * 100000),
                      currency: TCurrency.RUB,
                    },
                  ],
                ]),
              ])
            ),
          ],
        ]);
      } else {
        this.incomeSources = Object.keys(result).reduce(
          (yearsMap, year: string) =>
            yearsMap.set(
              parseInt(year, 10),
              Object.keys(result[year]).reduce(
                (monthsMap, month: string) =>
                  monthsMap.set(
                    parseInt(month, 10),
                    Object.keys(
                      result[year][month].sources || new Map()
                    ).reduce(
                      (accountsMap, incomeSourceUuid: string) =>
                        accountsMap.set(incomeSourceUuid, {
                          ...result[year][month].sources[incomeSourceUuid],
                          account: resultAccounts[incomeSourceUuid],
                          currency: resultCurrency,
                        }),
                      new Map<TAccount["uuid"], IAccountAmount>()
                    )
                  ),
                new Map<number, TAccountMap>()
              )
            ),
          new Map<number, TMonthAccountMap>()
        );

        this.expenseCategories = Object.keys(result).reduce(
          (yearsMap, year: string) =>
            yearsMap.set(
              parseInt(year, 10),
              Object.keys(result[year]).reduce(
                (monthsMap, month: string) =>
                  monthsMap.set(
                    parseInt(month, 10),
                    Object.keys(
                      result[year][month].categories || new Map()
                    ).reduce(
                      (accountsMap, expenseCategoryUuid: string) =>
                        accountsMap.set(expenseCategoryUuid, {
                          ...result[year][month].categories[
                            expenseCategoryUuid
                          ],
                          account: resultAccounts[expenseCategoryUuid],
                          currency: resultCurrency,
                        }),
                      new Map<TAccount["uuid"], IAccountAmount>()
                    )
                  ),
                new Map<number, TAccountMap>()
              )
            ),
          new Map<number, TMonthAccountMap>()
        );
      }
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.loading = false;
    }
  };

  public refresh = async (
    currency: TCurrency | undefined = undefined
  ): Promise<void> => {
    try {
      this.refreshing = true;

      await this.load(currency);
    } finally {
      this.refreshing = false;
    }
  };
}
